import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Building2, CreditCard, Image as ImageIcon, Save, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * Page : Coordonnées bancaires & logo des sociétés
 * Permet à Fred (et Sandrine) de saisir IBAN/BIC/logo/code facture par société
 * Ces infos alimentent automatiquement le PDF des factures (modèle Isagri).
 */
export default function CoordonneesSocietesPage() {
  const [societes, setSocietes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({});

  const loadSocietes = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/tenant/societes');
      const list = data?.societes || [];
      setSocietes(list);
      if (list.length > 0) {
        setDraft(extractFields(list[0]));
      }
    } catch (e) {
      toast.error("Impossible de charger les sociétés");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSocietes(); }, []);

  const extractFields = (s) => ({
    iban: s?.iban || '',
    bic: s?.bic || '',
    titulaire_compte: s?.titulaire_compte || s?.nom || '',
    banque_nom: s?.banque_nom || '',
    banque_adresse: s?.banque_adresse || '',
    siret: s?.siret || '',
    tva_intra: s?.tva_intra || s?.tva_intracommunautaire || '',
    logo_url: s?.logo_url || '',
    conditions_paiement: s?.conditions_paiement || 'Paiement à 30 jours fin de mois',
    code_facture: s?.code_facture || 'FA',
  });

  const switchSociete = (idx) => {
    setActiveIdx(idx);
    setDraft(extractFields(societes[idx]));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo trop lourd (max 2 Mo)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setDraft(prev => ({ ...prev, logo_url: reader.result }));
      toast.success("Logo chargé · n'oubliez pas d'enregistrer");
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    const societe = societes[activeIdx];
    if (!societe) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/tenant/societes/${societe.id}/coordonnees-bancaires`, draft);
      // Mettre à jour la liste locale
      const updated = data?.societe;
      if (updated) {
        const newList = [...societes];
        newList[activeIdx] = updated;
        setSocietes(newList);
        setDraft(extractFields(updated));
      }
      toast.success(`Coordonnées de "${societe.nom}" enregistrées`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const ibanValid = (iban) => {
    const clean = (iban || '').replace(/\s/g, '').toUpperCase();
    return clean.startsWith('FR') && clean.length === 27;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (societes.length === 0) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="p-12 text-center">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-slate-400" />
            <p className="text-slate-600">Aucune société accessible.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentSociete = societes[activeIdx];
  const fmt = extractFields(currentSociete);
  const ribOk = ibanValid(fmt.iban) && fmt.bic;
  const ribDirty = JSON.stringify(draft) !== JSON.stringify(fmt);

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="coordonnees-societes-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
          <CreditCard className="h-8 w-8 text-emerald-600" />
          Coordonnées bancaires & logo
        </h1>
        <p className="text-slate-600 mt-2">
          Ces informations apparaissent automatiquement sur les factures PDF (modèle Isagri) et le QR code SEPA "Scan to Pay".
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Liste des sociétés */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Mes sociétés ({societes.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <div className="space-y-1">
                {societes.map((s, idx) => {
                  const ok = !!s.iban && !!s.bic;
                  return (
                    <button
                      key={s.id}
                      onClick={() => switchSociete(idx)}
                      data-testid={`societe-tab-${s.id}`}
                      className={`w-full text-left px-3 py-2.5 rounded-md transition-colors ${
                        idx === activeIdx
                          ? 'bg-emerald-50 border border-emerald-200'
                          : 'hover:bg-slate-50 border border-transparent'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{s.nom}</p>
                          <p className="text-xs text-slate-500 truncate">{s.forme_juridique || s.activite || '—'}</p>
                        </div>
                        {ok ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Formulaire */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    {currentSociete.nom}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {currentSociete.adresse} · {currentSociete.code_postal} {currentSociete.ville}
                  </CardDescription>
                </div>
                {ribOk ? (
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" data-testid="badge-rib-ok">
                    <CheckCircle2 className="h-3 w-3 mr-1" /> RIB complet
                  </Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid="badge-rib-incomplete">
                    <AlertCircle className="h-3 w-3 mr-1" /> RIB incomplet
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="rib">
                <TabsList>
                  <TabsTrigger value="rib" data-testid="tab-rib">
                    <CreditCard className="h-4 w-4 mr-2" /> RIB / IBAN
                  </TabsTrigger>
                  <TabsTrigger value="logo" data-testid="tab-logo">
                    <ImageIcon className="h-4 w-4 mr-2" /> Logo & identité
                  </TabsTrigger>
                  <TabsTrigger value="facture" data-testid="tab-facture">
                    Numérotation factures
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="rib" className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="iban">IBAN <span className="text-red-500">*</span></Label>
                    <Input
                      id="iban"
                      data-testid="input-iban"
                      placeholder="FR76 1234 5678 9012 3456 7890 123"
                      value={draft.iban || ''}
                      onChange={(e) => setDraft({ ...draft, iban: e.target.value.toUpperCase() })}
                      className={!draft.iban || ibanValid(draft.iban) ? '' : 'border-red-500'}
                    />
                    {draft.iban && !ibanValid(draft.iban) && (
                      <p className="text-xs text-red-600 mt-1">IBAN français invalide (FR + 25 caractères)</p>
                    )}
                    <p className="text-xs text-slate-500 mt-1">
                      Utilisé sur le PDF + génération du QR Code SEPA "Scan to Pay" 📱
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="bic">BIC / SWIFT <span className="text-red-500">*</span></Label>
                      <Input
                        id="bic"
                        data-testid="input-bic"
                        placeholder="SOGEFRPP"
                        value={draft.bic || ''}
                        onChange={(e) => setDraft({ ...draft, bic: e.target.value.toUpperCase() })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="titulaire">Titulaire du compte</Label>
                      <Input
                        id="titulaire"
                        data-testid="input-titulaire"
                        placeholder={currentSociete.nom}
                        value={draft.titulaire_compte || ''}
                        onChange={(e) => setDraft({ ...draft, titulaire_compte: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="banque">Banque</Label>
                      <Input
                        id="banque"
                        data-testid="input-banque"
                        placeholder="Société Générale"
                        value={draft.banque_nom || ''}
                        onChange={(e) => setDraft({ ...draft, banque_nom: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="banque_adresse">Adresse banque (optionnel)</Label>
                      <Input
                        id="banque_adresse"
                        data-testid="input-banque-adresse"
                        placeholder="3 avenue de la République, 75011 Paris"
                        value={draft.banque_adresse || ''}
                        onChange={(e) => setDraft({ ...draft, banque_adresse: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="conditions">Conditions de paiement</Label>
                    <Input
                      id="conditions"
                      data-testid="input-conditions"
                      value={draft.conditions_paiement || ''}
                      onChange={(e) => setDraft({ ...draft, conditions_paiement: e.target.value })}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="logo" className="space-y-4 mt-4">
                  <div>
                    <Label>Logo de la société</Label>
                    <div className="mt-2 border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                      {draft.logo_url ? (
                        <div className="space-y-3">
                          <img
                            src={draft.logo_url}
                            alt="Logo"
                            className="max-h-32 mx-auto object-contain"
                            data-testid="logo-preview"
                          />
                          <div className="flex gap-2 justify-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => document.getElementById('logo-input').click()}
                              data-testid="btn-replace-logo"
                            >
                              Remplacer
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDraft({ ...draft, logo_url: '' })}
                              data-testid="btn-remove-logo"
                            >
                              Supprimer
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <ImageIcon className="h-12 w-12 mx-auto text-slate-400 mb-3" />
                          <Button
                            onClick={() => document.getElementById('logo-input').click()}
                            data-testid="btn-upload-logo"
                          >
                            Charger un logo (PNG/JPG, max 2 Mo)
                          </Button>
                        </div>
                      )}
                      <input
                        id="logo-input"
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        className="hidden"
                        onChange={handleLogoUpload}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="siret">SIRET</Label>
                      <Input
                        id="siret"
                        data-testid="input-siret"
                        value={draft.siret || ''}
                        onChange={(e) => setDraft({ ...draft, siret: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="tva">TVA Intracommunautaire</Label>
                      <Input
                        id="tva"
                        data-testid="input-tva"
                        placeholder="FR12345678901"
                        value={draft.tva_intra || ''}
                        onChange={(e) => setDraft({ ...draft, tva_intra: e.target.value.toUpperCase() })}
                      />
                      <p className="text-xs text-slate-500 mt-1">Auto-calculé depuis le SIRET si vide</p>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="facture" className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="code">Préfixe de numérotation des factures</Label>
                    <Input
                      id="code"
                      data-testid="input-code-facture"
                      placeholder="FA"
                      value={draft.code_facture || ''}
                      onChange={(e) => setDraft({ ...draft, code_facture: e.target.value.toUpperCase() })}
                      maxLength={10}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Exemple : "FA-ETA" donnera <code className="bg-slate-100 px-1 rounded">FA-ETA2026-00001</code> pour cette société.
                      Permet de différencier les numéros entre vos sociétés.
                    </p>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-emerald-900 mb-1">📱 QR Code SEPA "Scan to Pay"</p>
                    <p className="text-xs text-emerald-800">
                      Quand IBAN + BIC sont remplis, un QR code est automatiquement ajouté en bas de chaque facture.
                      Vos clients le scannent avec leur appli bancaire (CA, BNP, SG, La Banque Postale, BPCE, CM, iOS Wallet...) et le virement est pré-rempli avec le bon montant et la référence facture.
                    </p>
                    <p className="text-xs text-emerald-700 mt-2 italic">
                      Délai de paiement réduit en moyenne de 30j → 7j (étude SAGE 2025).
                    </p>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t">
                {ribDirty && (
                  <span className="text-sm text-amber-600 mr-auto">⚠️ Modifications non enregistrées</span>
                )}
                <Button
                  onClick={handleSave}
                  disabled={saving || !ribDirty}
                  data-testid="btn-save-coordonnees"
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Enregistrer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
